import type { UserProfile } from '@/lib/types'
import { Avatar } from './Avatar'
import { cn, profileName } from '@/lib/utils'

interface Props {
  /** User ids to render, in display order. */
  userIds: string[]
  /** Lookup for display name + avatar. */
  profilesById: Record<string, UserProfile>
  /** Optional id that gets an accent ring + an « Assigné » badge — used
   *  by card-level lists where one of the members is the responsible. */
  assigneeId?: string | null
  /** Optional id that gets a « Vous » badge — used to flag the current
   *  viewer in member lists (project-level / topbar usage). */
  currentUserId?: string | null
  /** Optional header label (« 2 membres » / « 5 collaborateurs ») —
   *  inferred when omitted from the userIds count. Pass `null` to hide
   *  the header entirely. */
  header?: string | null
  /** Max popover height in CSS — content scrolls past that. */
  maxHeight?: string
  className?: string
}

/** Reusable member list — one row per user, with avatar, full name,
 *  and optional badges for the assignee / current viewer.
 *
 *  Shared between the card's AvatarStack hover and the topbar's
 *  Membres button hover. Pure presentational, no popover positioning
 *  here — callers wrap it in their own anchored container. */
export function MemberList({
  userIds,
  profilesById,
  assigneeId = null,
  currentUserId = null,
  header,
  maxHeight = '16rem',
  className,
}: Props) {
  const computedHeader =
    header === null
      ? null
      : header ??
        (userIds.length === 1
          ? '1 membre'
          : `${userIds.length} membres`)

  return (
    <div
      className={cn(
        'min-w-[180px] max-w-[260px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 shadow-[var(--shadow-modal)]',
        className,
      )}
    >
      {computedHeader && (
        <div className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
          {computedHeader}
        </div>
      )}
      <ul
        className="overflow-y-auto"
        style={{ maxHeight }}
      >
        {userIds.map((id) => {
          const profile = profilesById[id]
          const name = profileName(profile) || 'Sans nom'
          const isAssignee = id === assigneeId
          const isYou = id === currentUserId
          return (
            <li
              key={id}
              className="flex items-center gap-2 px-3 py-1 text-[12px] text-[var(--color-text)]"
            >
              <Avatar
                profile={profile}
                userId={id}
                noTitle
                className={cn(
                  'h-5 w-5',
                  isAssignee &&
                    'ring-1 ring-offset-1 ring-offset-[var(--color-surface)] ring-[var(--color-accent)]',
                )}
                textClassName="text-[8px]"
              />
              <span className="min-w-0 flex-1 truncate">{name}</span>
              {isAssignee && (
                <span className="shrink-0 rounded bg-[var(--color-accent-bg)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-accent-text)]">
                  Assigné
                </span>
              )}
              {!isAssignee && isYou && (
                <span className="shrink-0 rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                  Vous
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
